import { useEffect, useState, type DragEvent } from "react";
import type { NewAppointmentSlotContext } from "./NewAppointmentModal";
import {
  type AppointmentEditContext,
  buildAppointmentEditContext,
  dailyLessonClass,
  isEvaluationPending,
  lessonTypeShort,
  type Reservation,
  reservationChipLines,
} from "./WeeklyCalendar";
import { LessonCancelledIcon } from "../icons/LessonCancelledIcon";
import { getIstanbulTodayYmd, isEmptySlotPastBookingDeadline } from "../../utils/slotBooking";
import {
  type AppointmentDragDropTarget,
  type AppointmentDragMode,
  type AppointmentDragSource,
  isDraggableReservationStatus,
} from "./appointmentDrag";
import "./DailyInstructorGrid.css";

function effectiveCellKind(
  gridDate: string,
  cell: DayGridCell,
): DayGridCell["kind"] {
  if (
    (cell.kind === "available" || cell.kind === "rebookable") &&
    isEmptySlotPastBookingDeadline(gridDate, cell.start)
  ) {
    if (cell.kind === "rebookable") return "occupied";
    return "blocked";
  }
  return cell.kind;
}

export type DayGridCell = {
  index: number;
  start: string;
  end: string;
  kind: "blocked" | "leave" | "occupied" | "completed" | "available" | "rebookable";
  reservations: Reservation[];
};

export type DayGridRow = {
  instructor_id: string;
  instructor_name: string;
  slots: DayGridCell[];
};

export type DayGridSettingsMeta = {
  effective_from?: string;
  daily_lesson_count?: number;
  day_start_time?: string;
  day_end_time?: string;
};

export type DayGridResponse = {
  date: string;
  slot_columns: Array<{ index: number; start: string; end: string }>;
  settings?: DayGridSettingsMeta;
  rows: DayGridRow[];
};

type Props = {
  data: DayGridResponse;
  onReserve: (ctx: NewAppointmentSlotContext) => void;
  onOpenLessonReport?: (appt: {
    id: string;
    starts_at: string;
    ends_at: string;
    lesson_type: string;
    status: string;
    student_id?: string;
    student_name?: string | null;
  }) => void;
  onShowAuditHistory?: (appointmentId: string) => void;
  onEditReservation?: (ctx: AppointmentEditContext) => void;
  onCancelAppointment?: (appt: {
    id: string;
    student_name?: string | null;
    starts_at: string;
    ends_at: string;
    status: string;
  }) => void;
  onReactivateAppointment?: (appt: {
    id: string;
    student_name?: string | null;
    starts_at: string;
    ends_at: string;
  }) => void;
  /** K=kopyala, T=taşı — tuş basılıyken sürükle-bırak. */
  onAppointmentDragDrop?: (target: AppointmentDragDropTarget) => void;
};

type ActiveDrag = {
  mode: AppointmentDragMode;
  source: AppointmentDragSource;
};

function modeFromKeyboardEvent(e: KeyboardEvent): AppointmentDragMode | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  const key = e.key.length === 1 ? e.key.toLocaleLowerCase("tr-TR") : e.key;
  if (key === "k") return "copy";
  if (key === "t") return "move";
  return null;
}

export function DailyInstructorGrid({
  data,
  onReserve,
  onOpenLessonReport,
  onShowAuditHistory,
  onEditReservation,
  onCancelAppointment,
  onReactivateAppointment,
  onAppointmentDragDrop,
}: Props) {
  const cols = data.slot_columns;
  const [, setClockTick] = useState(0);
  const [heldMode, setHeldMode] = useState<AppointmentDragMode | null>(null);
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  useEffect(() => {
    if (!onAppointmentDragDrop) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT" || target.isContentEditable)) {
        return;
      }
      const mode = modeFromKeyboardEvent(e);
      if (mode) {
        e.preventDefault();
        setHeldMode(mode);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      const key = e.key.length === 1 ? e.key.toLocaleLowerCase("tr-TR") : e.key;
      if (key === "k" || key === "t") {
        setHeldMode(null);
      }
    };
    const onBlur = () => setHeldMode(null);

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [onAppointmentDragDrop]);

  const renderReservation = (
    r: Reservation,
    cell: DayGridCell,
    primary: string,
    options?: { instructorId: string; allowNewReservation?: boolean },
  ) => {
    const { secondary } = reservationChipLines(r);
    const isCancelled = r.status === "cancelled";
    const isCompleted = r.status === "completed";
    const isNoShow = isCompleted && r.report_attended === false;
    const showCompletedMark = isCompleted && !isNoShow;
    const needsEvaluation = !isCancelled && isEvaluationPending(r);
    const canDrag =
      Boolean(onAppointmentDragDrop) &&
      isDraggableReservationStatus(r.status) &&
      Boolean(r.student_id && r.vehicle_id && (r.instructor_id || options?.instructorId));
    const dragEnabled = canDrag && heldMode != null;

    return (
      <div
        key={r.id}
        className={`kp-daily-cal__busy ${dailyLessonClass(r.lesson_type)}${isCancelled ? " kp-daily-cal__busy--cancelled" : ""}${isCancelled ? " kp-daily-cal__busy--compact" : ""}${dragEnabled ? " kp-daily-cal__busy--draggable" : ""}${activeDrag?.source.appointmentId === r.id ? " kp-daily-cal__busy--dragging" : ""}`}
        draggable={dragEnabled}
        title={
          canDrag
            ? "K basılı tutup sürükle: kopyala · T basılı tutup sürükle: taşı"
            : undefined
        }
        onDragStart={(event) => {
          if (!onAppointmentDragDrop || !canDrag || !heldMode) {
            event.preventDefault();
            return;
          }
          if (!r.student_id || !r.vehicle_id) {
            event.preventDefault();
            return;
          }
          const instructorId = r.instructor_id || options?.instructorId || "";
          if (!instructorId) {
            event.preventDefault();
            return;
          }
          const source: AppointmentDragSource = {
            appointmentId: r.id,
            studentId: r.student_id,
            studentName: primary,
            instructorId,
            vehicleId: r.vehicle_id,
            lessonType: r.lesson_type,
            sourceDate: data.date,
            sourceStart: cell.start,
            sourceEnd: cell.end,
          };
          setActiveDrag({ mode: heldMode, source });
          event.dataTransfer.effectAllowed = heldMode === "copy" ? "copy" : "move";
          event.dataTransfer.setData(
            "application/x-kp-appointment-drag",
            JSON.stringify({ mode: heldMode, source }),
          );
          try {
            event.dataTransfer.setData("text/plain", primary);
          } catch {
            /* ignore */
          }
        }}
        onDragEnd={() => {
          setActiveDrag(null);
          setDragOverKey(null);
        }}
      >
        <div className="kp-daily-cal__busy-body">
          {isCancelled && (
            <span
              className="kp-daily-cal__cancel-mark"
              aria-label="Ders İptal Edildi"
              title="Ders İptal Edildi"
            >
              <LessonCancelledIcon size={18} />
            </span>
          )}
          {needsEvaluation && (
            <span
              className="kp-daily-cal__eval-mark"
              aria-label="Değerlendirme bekliyor"
              title="Değerlendirme bekliyor"
            >
              !
            </span>
          )}
          {isNoShow && (
            <span className="kp-daily-cal__noshow-mark" aria-label="Kursiyer gelmedi" title="Kursiyer gelmedi">
              ✕
            </span>
          )}
          {showCompletedMark && (
            <span className="kp-daily-cal__complete-mark" aria-label="Tamamlandı" title="Tamamlandı">
              ✓
            </span>
          )}
          <span className="kp-daily-cal__lesson-tag">{lessonTypeShort(r.lesson_type)}</span>
          <div className="kp-daily-cal__busy-name">{primary}</div>
          {secondary ? <div className="kp-daily-cal__busy-sub">{secondary}</div> : null}
        </div>
        {(onOpenLessonReport ||
          onShowAuditHistory ||
          onEditReservation ||
          onCancelAppointment ||
          onReactivateAppointment ||
          (isCancelled && options?.allowNewReservation)) && (
          <div className="kp-daily-cal__cell-actions">
            {isCancelled && (onReactivateAppointment || options?.allowNewReservation) && (
              <div
                className="kp-daily-cal__cell-actions-row kp-daily-cal__cell-actions-row--cancelled"
                role="group"
                aria-label="İptal edilmiş ders işlemleri"
              >
                {onReactivateAppointment && (
                  <button
                    type="button"
                    className="kp-daily-cal__mini kp-daily-cal__mini--reactivate"
                    title="Dersi Yeniden Aktifleştir"
                    onClick={() =>
                      onReactivateAppointment({
                        id: r.id,
                        student_name: primary,
                        starts_at: r.starts_at,
                        ends_at: r.ends_at,
                      })
                    }
                  >
                    Yeniden Aktifleştir
                  </button>
                )}
                {options?.allowNewReservation && options.instructorId && (
                  <button
                    type="button"
                    className="kp-daily-cal__reserve kp-daily-cal__reserve--inline"
                    title="Bu slota yeni rezervasyon yap"
                    onClick={() =>
                      onReserve({
                        date: data.date,
                        startHHmm: cell.start,
                        endHHmm: cell.end,
                        preferredInstructorId: options.instructorId,
                      })
                    }
                  >
                    + Rezervasyon
                  </button>
                )}
              </div>
            )}
            {!isCancelled && (
              <div className="kp-daily-cal__cell-actions-row" role="group" aria-label="Randevu işlemleri">
                {onOpenLessonReport && (
                  <button
                    type="button"
                    className={`kp-daily-cal__mini kp-daily-cal__mini--on-lesson${isCompleted ? " kp-daily-cal__mini--report-done" : ""}`}
                    title="Ders değerlendirmesi"
                    onClick={() =>
                      onOpenLessonReport({
                        id: r.id,
                        starts_at: r.starts_at,
                        ends_at: r.ends_at,
                        lesson_type: r.lesson_type,
                        status: r.status,
                        student_id: r.student_id,
                        student_name: primary,
                      })
                    }
                  >
                    Ders Raporu
                  </button>
                )}
                {onEditReservation && (
                  <button
                    type="button"
                    className="kp-daily-cal__mini kp-daily-cal__mini--on-lesson"
                    title="Düzenle"
                    onClick={() => {
                      const ctx = buildAppointmentEditContext(r, data.date, cell.start, cell.end);
                      if (ctx) onEditReservation(ctx);
                    }}
                  >
                    Düzenle
                  </button>
                )}
                {onCancelAppointment && r.status !== "completed" && (
                  <button
                    type="button"
                    className="kp-daily-cal__mini kp-daily-cal__mini--cancel"
                    title="Dersi İptal Et"
                    onClick={() =>
                      onCancelAppointment({
                        id: r.id,
                        student_name: primary,
                        starts_at: r.starts_at,
                        ends_at: r.ends_at,
                        status: r.status,
                      })
                    }
                  >
                    Dersi İptal Et
                  </button>
                )}
                {onShowAuditHistory && (
                  <button
                    type="button"
                    className="kp-daily-cal__mini kp-daily-cal__mini--on-lesson"
                    title="Geçmişi Gör"
                    onClick={() => onShowAuditHistory(r.id)}
                  >
                    Geçmişi Gör
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (data.date !== getIstanbulTodayYmd()) return;
    const id = window.setInterval(() => setClockTick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, [data.date]);

  const handleDropOnAvailable = (
    event: DragEvent,
    row: DayGridRow,
    cell: DayGridCell,
  ) => {
    event.preventDefault();
    setDragOverKey(null);
    if (!onAppointmentDragDrop || !activeDrag) return;

    const sameSlot =
      activeDrag.source.sourceDate === data.date &&
      activeDrag.source.sourceStart === cell.start &&
      activeDrag.source.sourceEnd === cell.end &&
      activeDrag.source.instructorId === row.instructor_id;
    if (sameSlot) {
      setActiveDrag(null);
      return;
    }

    onAppointmentDragDrop({
      mode: activeDrag.mode,
      source: activeDrag.source,
      date: data.date,
      startHHmm: cell.start,
      endHHmm: cell.end,
      instructorId: row.instructor_id,
      instructorName: row.instructor_name,
    });
    setActiveDrag(null);
  };

  return (
    <div className={`kp-daily-cal${heldMode ? ` kp-daily-cal--drag-${heldMode}` : ""}`}>
      {onAppointmentDragDrop && (
        <p className="kp-daily-cal__drag-hint" role="status">
          {heldMode === "copy"
            ? "Kopyalama modu (K) — randevuyu boş slota sürükleyin"
            : heldMode === "move"
              ? "Taşıma modu (T) — randevuyu boş slota sürükleyin"
              : "Kopyalamak için K, taşımak için T tuşuna basılı tutup randevuyu boş slota sürükleyin"}
        </p>
      )}
      <div className="kp-daily-cal__table-wrap kp-card">
        <table className="kp-daily-cal__table">
          <colgroup>
            <col className="kp-daily-cal__col-instr" />
            {cols.map((c) => (
              <col key={c.index} className="kp-daily-cal__col-slot" />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="kp-daily-cal__th kp-daily-cal__th--corner" />
              {cols.map((c) => (
                <th key={c.index} className="kp-daily-cal__th kp-daily-cal__th--time">
                  <span className="kp-daily-cal__time-range">
                    {c.start}–{c.end}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.instructor_id}>
                <th className="kp-daily-cal__instr" scope="row">
                  <span className="kp-daily-cal__instr-name">{row.instructor_name.toLocaleUpperCase("tr-TR")}</span>
                </th>
                {row.slots.map((cell) => {
                  const kind = effectiveCellKind(data.date, cell);
                  const dropKey = `${row.instructor_id}:${cell.index}`;
                  const canDrop = Boolean(activeDrag) && kind === "available";
                  const isOver = canDrop && dragOverKey === dropKey;
                  return (
                  <td key={cell.index} className="kp-daily-cal__td">
                    <div
                      className={`kp-daily-cal__cell kp-daily-cal__cell--${kind}${isOver ? " kp-daily-cal__cell--drag-over" : ""}`}
                      onDragOver={
                        canDrop
                          ? (event) => {
                              event.preventDefault();
                              event.dataTransfer.dropEffect = activeDrag?.mode === "copy" ? "copy" : "move";
                              if (dragOverKey !== dropKey) setDragOverKey(dropKey);
                            }
                          : undefined
                      }
                      onDragLeave={
                        canDrop
                          ? () => {
                              if (dragOverKey === dropKey) setDragOverKey(null);
                            }
                          : undefined
                      }
                      onDrop={
                        canDrop
                          ? (event) => handleDropOnAvailable(event, row, cell)
                          : undefined
                      }
                    >
                      {kind === "blocked" && <span className="kp-daily-cal__dash">–</span>}
                      {kind === "leave" && (
                        <div className="kp-daily-cal__leave-wrap">
                          <span className="kp-daily-cal__leave">EĞİTMEN İZİNLİ</span>
                          {!isEmptySlotPastBookingDeadline(data.date, cell.start) && (
                            <button
                              type="button"
                              className="kp-daily-cal__reserve kp-daily-cal__reserve--leave"
                              onClick={() => {
                                const ok = window.confirm(
                                  "Eğitmen bu saatte izinli.\n\nİstisna olarak yalnızca bu slota randevu oluşturmak istiyor musunuz?\n(Haftalık / dönem izni diğer saatlerde aynı kalır.)",
                                );
                                if (!ok) return;
                                onReserve({
                                  date: data.date,
                                  startHHmm: cell.start,
                                  endHHmm: cell.end,
                                  preferredInstructorId: row.instructor_id,
                                  leaveException: true,
                                });
                              }}
                            >
                              + Rezervasyon
                            </button>
                          )}
                        </div>
                      )}
                      {kind === "available" && (
                        <button
                          type="button"
                          className="kp-daily-cal__reserve"
                          onClick={() =>
                            onReserve({
                              date: data.date,
                              startHHmm: cell.start,
                              endHHmm: cell.end,
                              preferredInstructorId: row.instructor_id,
                            })
                          }
                        >
                          {isOver
                            ? activeDrag?.mode === "copy"
                              ? "Kopyala"
                              : "Taşı"
                            : "+ Rezervasyon"}
                        </button>
                      )}
                      {kind === "rebookable" && (
                        <div className="kp-daily-cal__rebookable">
                          {cell.reservations.map((r) => {
                            const { primary } = reservationChipLines(r);
                            return renderReservation(r, cell, primary, {
                              instructorId: row.instructor_id,
                              allowNewReservation: true,
                            });
                          })}
                        </div>
                      )}
                      {(kind === "occupied" || kind === "completed") &&
                        cell.reservations.map((r) => {
                          const { primary } = reservationChipLines(r);
                          return renderReservation(r, cell, primary, {
                            instructorId: row.instructor_id,
                          });
                        })}
                    </div>
                  </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
